import type { ReactNode } from "react";
import { LedgerRow } from "../internal/BaseLedgerRow";
import { formatLedgerWhen, formatSitePlot } from "../format";
import {
  Money,
  PartTag,
  PercentCaption,
  Quantity,
  StatusDot,
} from "../primitives";
import {
  composeRowAriaLabel,
  isCancelledStatus,
  statusToken,
  statusWord,
} from "../statusTokens";
import type { LedgerCollapse, LedgerColumn, LedgerRowMenu } from "../types";
import { composeMetaLine } from "./metaLine";
import { workCells, type SitePlot } from "./workCells";

export interface BookingLedgerRowProps {
  columns: readonly LedgerColumn[];
  collapse: LedgerCollapse;
  /** The labour item name. Part-booked items repeat it on every peer row. */
  title: string;
  /**
   * A `WorkStatus` where the screen has one, any status string otherwise.
   * Omitted only while `pending`: an item can render before the booking that
   * gives it a status has arrived.
   */
  status?: string;
  /** Absent until the booking arrives, and on work that has no allocation. */
  reference?: string | null;
  bookedBy?: string | null;
  /** ISO timestamp. */
  bookedAt?: string | null;
  /** Null when the viewer may not see money; undefined while `pending`. */
  amount?: number | null;
  /** Quantity replaces money as the primary value for unit-priced work. */
  quantity?: { value: number; unitLabel: string } | null;
  /** "1 of 2" when an item is booked across several peer rows. */
  partLabel?: string;
  /** Booked share of the item's target, 0-100, when this row is partial. */
  percentBooked?: number | null;
  sitePlot?: SitePlot;
  showValues?: boolean;
  /** The booking behind the row has not arrived: its cells show placeholders. */
  pending?: boolean;
  hoverCard?: ReactNode;
  menu?: LedgerRowMenu;
  onClick?: () => void;
  "data-testid"?: string;
}

/** Part-booked rows carry either a "1 of 2" tag or a raw percentage, never both. */
function titleTag(
  partLabel: string | undefined,
  percentBooked: number | null | undefined,
): ReactNode {
  if (partLabel) {
    return <PartTag>{partLabel}</PartTag>;
  }
  if (percentBooked != null) {
    return <PercentCaption percent={percentBooked} />;
  }
  return undefined;
}

/** A booking against a labour item: status dot, item name, ref, who, when, value. */
export const BookingLedgerRow = ({
  columns,
  collapse,
  title,
  status,
  reference,
  bookedBy,
  bookedAt,
  amount,
  quantity,
  partLabel,
  percentBooked,
  sitePlot,
  showValues = true,
  pending = false,
  hoverCard,
  menu,
  onClick,
  "data-testid": dataTestId,
}: BookingLedgerRowProps) => {
  const cancelled = isCancelledStatus(status);
  const meta = composeMetaLine([
    reference,
    status != null ? statusWord(status) : null,
    bookedBy,
    formatLedgerWhen(bookedAt),
    sitePlot ? formatSitePlot(sitePlot.site, sitePlot.plot) : null,
  ]);

  return (
    <LedgerRow
      columns={columns}
      collapse={collapse}
      lead={
        status != null ? (
          <StatusDot color={statusToken(status).color} />
        ) : undefined
      }
      title={title}
      titleTag={titleTag(partLabel, percentBooked)}
      cells={workCells({ reference, bookedBy, bookedAt, sitePlot })}
      value={
        pending && amount === undefined ? undefined : quantity ? (
          <Quantity
            value={quantity.value}
            unitLabel={quantity.unitLabel}
            strike={cancelled}
          />
        ) : (
          <Money value={amount} strike={cancelled} />
        )
      }
      meta={meta}
      dimmed={cancelled}
      showValues={showValues}
      pending={pending}
      hoverCard={hoverCard}
      menu={menu}
      onClick={onClick}
      aria-label={composeRowAriaLabel(title, status, [reference])}
      data-status={status}
      data-testid={dataTestId}
    />
  );
};
